if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Card selection and cart submission have independent lifetimes, as in
      // Horizon. A pending selection may queue one Add; an active cart request
      // remains locked even when the card changes its displayed variant.
      beginCardVariantChange(promise) {
        if (this.cardCanAdd == null) this.cardCanAdd = !this.submitButton.disabled;
        const generation = (this.cardVariantGeneration || 0) + 1;
        this.cardVariantGeneration = generation;
        const selection = { generation, promise: promise.catch(() => null) };
        this.cardVariantChange = selection;
        selection.promise.then(() => {
          if (this.cardVariantChange === selection) this.cardVariantChange = undefined;
        });
      }

      updateCardSubmitState(canAdd) {
        this.cardCanAdd = canAdd;
        this.submitButton.disabled = !canAdd || !!this.cardAddInProgress;
        if (this.cardAddInProgress) this.submitButton.setAttribute('aria-disabled', 'true');
        else this.submitButton.removeAttribute('aria-disabled');
      }

      async queueCardAdd(evt, quantity, selection) {
        if (this.cardQueuedAdd) return;
        const queued = { quantity: quantity.input.value, selection };
        this.cardQueuedAdd = queued;
        try {
          const state = await selection.promise;
          // Superseded or unresolved choices never fall through to the old ID.
          if (!state || selection.generation !== this.cardVariantGeneration || !this.isConnected) return;
          quantity.input.value = queued.quantity;
          quantity.normalizeValue();
          return this.onSubmitHandler(evt, true);
        } finally {
          if (this.cardQueuedAdd === queued) this.cardQueuedAdd = undefined;
        }
      }

      onSubmitHandler(evt, resolvedCardSelection = false) {
        evt.preventDefault();
        if (this.cardAddInProgress) return;
        const cardQuantity = this.querySelector('horizon-product-card-quantity');
        if (cardQuantity && this.cardVariantChange && !resolvedCardSelection) {
          // Capture the quantity at click time before a variant update resets it.
          return this.queueCardAdd(evt, cardQuantity, this.cardVariantChange);
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (cardQuantity && (this.submitButton.disabled || this.variantIdInput.disabled || !cardQuantity.validateForSubmit())) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const cardGeneration = this.cardVariantGeneration;
        if (cardQuantity) {
          if (this.cardCanAdd == null) this.cardCanAdd = true;
          this.cardAddInProgress = true;
          this.submitButton.disabled = true;
        }

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              if (!cardQuantity || cardGeneration === this.cardVariantGeneration) {
                this.handleErrorMessage(response.description);
              }

              if (cardQuantity) return;

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_page_url || window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (cardQuantity) {
              this.cardAddInProgress = false;
              this.updateCardSubmitState(this.cardCanAdd ?? true);
            }
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButton.dataset.submitLabel) {
            this.submitButtonText.textContent = this.submitButton.dataset.submitLabel;
          } else if (this.submitButton.dataset.bundleButton) {
            this.submitButtonText.textContent = window.variantStrings.addToBundle;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
